import { deviceActionDefinition } from '../../../nodes/definitions/actions/device-action';
import { deviceConditionDefinition } from '../../../nodes/definitions/conditions/device-condition';
import {
	resolveActionCapabilitySelection,
	resolveConditionCapabilitySelection,
} from '../device-capabilities';
import type { FlowCardDefinition } from '../types';

const VERSION = '1.0.0';

function conditionSummary(data: Record<string, any>): string {
	const selection = resolveConditionCapabilitySelection(data);
	if (!selection.device) return 'Choose a device';
	if (!selection.property)
		return `Choose what to track on ${selection.device.name || selection.device.id}`;
	const value = selection.value === undefined || selection.value === '' ? 'a value' : selection.value;
	return `${selection.device.name || selection.device.id}: ${selection.property} ${selection.operator || 'equals'} ${value}`;
}

function actionSummary(data: Record<string, any>): string {
	const selection = resolveActionCapabilitySelection(data);
	if (!selection.device) return 'Choose a device';
	if (!selection.actions.length)
		return `Choose an action for ${selection.device.name || selection.device.id}`;
	return `${selection.device.name || selection.device.id}: ${selection.actions
		.map((action) => `${action.name} ${String(action.value)}`)
		.join(', ')}`;
}

function conditionCard(deviceCheck: boolean): FlowCardDefinition {
	return {
		type: 'condition',
		...(deviceCheck ? { paletteKey: 'deviceCheck' } : {}),
		version: VERSION,
		domain: 'core',
		role: 'condition',
		category: 'conditions',
		label: deviceCheck ? 'Device Check' : 'Device Condition',
		description: deviceCheck
			? 'Check a device value once, on every change, or continuously'
			: 'React when a selected device value matches',
		icon: deviceCheck ? 'fa-shield-halved' : 'fa-microchip',
		color: deviceCheck ? '#6366f1' : '#3b82f6',
		discovery: {
			role: deviceCheck ? 'condition' : 'trigger',
			entityKinds: ['device'],
		},
		schema: {
			$schema: 'https://json-schema.org/draft/2020-12/schema',
			type: 'object',
			properties: {
				device: { type: 'object' },
				_device_condition: { type: 'object' },
				sceneCapability: { type: 'object' },
				cond_name: { type: 'string' },
				operator: { type: 'string' },
				executionMode: { type: 'string', enum: ['once', 'any', 'repeat'] },
				hold_off: { type: 'number', minimum: 0 },
			},
		},
		fields: [
			{
				path: '_device_condition',
				label: 'Device condition',
				type: 'device-condition-configuration',
			},
		],
		ports: { sources: ['true', 'false'] },
		defaults: () => ({
			triggerType: 'device',
			...(deviceCheck ? { executionMode: 'once', hold_off: 5 } : { executionMode: 'any' }),
			cardVersion: VERSION,
		}),
		summary: conditionSummary,
		validate: (data) => {
			if (data.sceneData || data.originalCondition) return [];
			const selection = resolveConditionCapabilitySelection(data);
			const issues = [];
			if (!selection.device) issues.push({ path: 'device', message: 'Select a device' });
			if (!selection.property)
				issues.push({ path: 'cond_name', message: 'Select what the condition should track' });
			if (!selection.operator)
				issues.push({ path: 'operator', message: 'Select how the value should be compared' });
			return issues;
		},
		compile: (node, context) => {
			const result = deviceConditionDefinition.toSceneJson(node, context);
			return result && typeof result === 'object'
				? {
						...result,
						_fid: (result as any)._fid || node.id,
						_flow_card: deviceCheck ? 'deviceCheck' : 'condition',
						_flow_card_version: VERSION,
					}
				: result;
		},
		migrate: (data) => {
			const legacyMode = data.executionMode ?? (data.cond_type as any)?.id;
			const executionMode =
				legacyMode === 'repeat' || legacyMode === 3
					? 'repeat'
					: legacyMode === 'any' || legacyMode === 2
						? 'any'
						: 'once';
			return {
				...data,
				triggerType: 'device',
				executionMode: deviceCheck ? executionMode : 'any',
				...(deviceCheck ? { hold_off: Number(data.hold_off ?? 5) } : {}),
			};
		},
	};
}

export const deviceConditionCards = [conditionCard(false), conditionCard(true)];

export const deviceActionCard: FlowCardDefinition = {
	type: 'action',
	version: VERSION,
	domain: 'core',
	role: 'action',
	category: 'actions',
	label: 'Device Action',
	description: 'Control a supported channel or property on a device',
	icon: 'fa-toggle-on',
	color: '#22c55e',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		properties: {
			device: { type: 'object' },
			actions: { type: 'array' },
			delay: { type: 'object' },
			timer: { type: 'object' },
		},
	},
	fields: [{ path: 'actions', label: 'Device action', type: 'device-action-configuration' }],
	ports: { sources: ['output'] },
	defaults: () => ({ actions: [], cardVersion: VERSION }),
	summary: actionSummary,
	validate: (data) => {
		if (data.sceneData || data.originalAction) return [];
		const selection = resolveActionCapabilitySelection(data);
		const issues = [];
		if (!selection.device) issues.push({ path: 'device', message: 'Select a device' });
		if (!selection.actions.length) issues.push({ path: 'actions', message: 'Select an action' });
		return issues;
	},
	compile: (node, context) => {
		const result = deviceActionDefinition.toSceneJson(node, context);
		return result && typeof result === 'object'
			? {
					...result,
					_fid: (result as any)._fid || node.id,
					_flow_card: 'action',
					_flow_card_version: VERSION,
				}
			: result;
	},
	migrate: (data) => ({ ...data, actions: Array.isArray(data.actions) ? data.actions : [] }),
};
